/**
 * Parsing for a DRep record as the index returns it.
 *
 * Split out of `catalog.ts` because the browser needs it too: the directory
 * search runs server-side on first paint and client-side as you type, and both
 * read the same upstream shape. `catalog.ts` carries the server cache and the
 * upstream base URL, neither of which belongs in a client bundle.
 */

export interface DRepSeoRecord {
  /** CIP-129 identifier — the canonical form used in URLs. */
  drepId: string;
  drepIdCip105?: string | null;
  drepIdHex?: string | null;
  /**
   * `dRepName` from the DRep's own metadata document.
   *
   * Self-declared and unverified: it is NOT a minted handle, is not unique,
   * and proves nothing about ownership. Usable as a display name only — never
   * to route, canonicalise or identify. See `resolveHandleOwner`.
   */
  declaredName?: string | null;
  givenName?: string | null;
  bio?: string | null;
  objectives?: string | null;
  motivations?: string | null;
  qualifications?: string | null;
  image?: string | null;
  references?: unknown;
  isScript?: boolean;
  updatedAt?: string | null;
  /**
   * Registration status as the indexer reports it: `Active`, `Inactive` or
   * `Retired`. Overwhelmingly not `Active` — on preview, 158 of 10,000 — which
   * is why the directory defaults to the active ones.
   */
  status?: DRepStatus | null;
}

export type DRepStatus = 'Active' | 'Inactive' | 'Retired';

const DREP_STATUSES: DRepStatus[] = ['Active', 'Inactive', 'Retired'];

function statusValue(value: unknown): DRepStatus | null {
  const text = textValue(value);
  return DREP_STATUSES.find((status) => status === text) ?? null;
}

export function objectValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function textValue(value: unknown): string | null {
  return typeof value === 'string' ? value.trim() || null : null;
}

export function normalizeDRep(value: unknown): DRepSeoRecord | null {
  const source = objectValue(value);
  const meta = objectValue(source.meta);
  const drepId = textValue(meta.drepIdCip129) || textValue(meta.drepIdCip105);
  if (!drepId) return null;
  return {
    drepId,
    drepIdCip105: textValue(meta.drepIdCip105),
    drepIdHex: textValue(meta.drepIdHex),
    declaredName: textValue(source.dRepName),
    givenName: textValue(source.givenName),
    bio: textValue(source.bio),
    objectives: textValue(source.objectives),
    motivations: textValue(source.motivations),
    qualifications: textValue(source.qualifications),
    image: textValue(source.image),
    references: source.references ?? null,
    isScript: meta.isScript === true,
    updatedAt: textValue(meta.fetchedAt),
    status: statusValue(source.status),
  };
}

/** One hit from the indexed search, with the fields that matched. */
export interface DRepSearchHit {
  record: DRepSeoRecord;
  /** Field path → ranked snippets, each with the match wrapped in `<em>`. */
  highlights: { field: string; snippets: string[] }[];
}

/**
 * Index bookkeeping that matches the query but means nothing to a reader — the
 * anchor URL and its hash. Identifier matches are kept: searching an id and
 * seeing which one hit is the point.
 */
const NOISE_HIGHLIGHT_FIELDS = ['meta.fetchedUrl', 'meta.dataHash'];

export function readHighlights(entry: unknown): DRepSearchHit['highlights'] {
  const highlight = objectValue(objectValue(entry).highlight);
  return Object.keys(highlight)
    .filter((field) => !NOISE_HIGHLIGHT_FIELDS.includes(field))
    .map((field) => {
      const value = highlight[field];
      const snippets = (Array.isArray(value) ? value : [value])
        .map(textValue)
        .filter((snippet): snippet is string => snippet !== null);
      return { field, snippets };
    })
    .filter((entry) => entry.snippets.length > 0);
}
